import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            toast.error('Token de restablecimiento no encontrado');
            navigate('/login');
        }
    }, [token, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!password || !confirmPassword) {
            toast.error('Por favor completa todos los campos');
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        if (password.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        setLoading(true);
        try {
            await api.post('/auth/reset-password', { token, password });
            setSuccess(true);
            toast.success('Contraseña actualizada con éxito');
        } catch (error) {
            const message = error.response?.data?.detail || 'Error al restablecer la contraseña';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-wedding-background flex items-center justify-center p-8">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md bg-white p-8 rounded-2xl shadow-luxury border border-wedding-border"
            >
                <div className="flex items-center gap-2 mb-8 justify-center">
                    <Heart className="w-6 h-6 text-wedding-primary" fill="currentColor" />
                    <span className="font-heading text-2xl text-wedding-text">Wedvites</span>
                </div>

                {!success ? (
                    <>
                        <div className="mb-8">
                            <h1 className="font-heading text-3xl text-wedding-text mb-2 text-center">
                                Nueva Contraseña
                            </h1>
                            <p className="text-wedding-text-muted text-center">
                                Crea una nueva contraseña segura para tu cuenta.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="password">Nueva Contraseña</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-wedding-text-muted" />
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10 pr-10 py-6 border-wedding-border focus:border-wedding-primary"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-wedding-text-muted hover:text-wedding-text"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-wedding-text-muted" />
                                    <Input
                                        id="confirmPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="pl-10 py-6 border-wedding-border focus:border-wedding-primary"
                                        required
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full py-6 bg-wedding-primary hover:bg-wedding-primary-dark text-white rounded-full text-sm uppercase tracking-widest font-semibold transition-all duration-300"
                            >
                                {loading ? 'Actualizando...' : 'Cambiar Contraseña'}
                            </Button>
                        </form>
                    </>
                ) : (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600">
                            <CheckCircle2 className="w-8 h-8" />
                        </div>
                        <h2 className="font-heading text-2xl text-wedding-text mb-4">¡Listo!</h2>
                        <p className="text-wedding-text-muted mb-8">
                            Tu contraseña ha sido actualizada correctamente. Ya puedes iniciar sesión con tus nuevas credenciales.
                        </p>
                        <Link to="/login">
                            <Button className="w-full rounded-full bg-wedding-primary hover:bg-wedding-primary-dark text-white">
                                Ir a Iniciar Sesión
                            </Button>
                        </Link>
                    </div>
                )}
            </motion.div>
        </div>
    );
}
